import { LoaderFunctionArgs } from "react-router-dom";
import { config } from "../config";
import { waitForNormalizedAuthClaims, waitForStoredOidcUser } from "../auth/authClaims";

export interface Simulation {
    id: string;
    name: string;
    owner?: string;
    totalExecutions: number;
    // createdAt (UTC, "YYYY-MM-DD HH:MM:SS") of the newest execution; null for
    // executions stored before it was recorded.
    lastExecutionAt?: string | null;
}

export interface TimeSeriesInput {
    x: number[];
    y: number[];
}

// One labelled signal. x is simulation time in milliseconds; between points the
// value is held (the y of the latest x <= t, the first y before the first x).
export interface InputSeries extends TimeSeriesInput {
    label: string;
}

// The inputs and parameters for a simulation's next execution: the same shape
// as an execution without results.
export interface InputsDocument {
    executionTime?: string;
    inputs: InputSeries[];
    parameters: Record<string, number>;
}

export type VerificationKind = 'always' | 'response' | 'settle' | 'mode' | 'oscillation' | 'wear' | 'rate' | 'energy' | 'percent';

// Outcome of one verification rule at the end of a run.
export interface VerificationOutcome {
    kind: VerificationKind | string;
    // error: a signal rule's signal was never there.
    status: 'pass' | 'fail' | 'error';
    pass: boolean;
    // Headroom in physical units: positive satisfied, negative violated by
    // that much, null not finite.
    margins: Record<string, number | null>;
    metrics: Record<string, unknown>;
    // [from, to] in seconds of simulation time where the rule failed; an end
    // is null when not finite.
    violations: [number | null, number | null][];
    explanation: string;
}

// How one run covered one requirement it named.
export interface RequirementCoverage {
    // Named before the first step, so it covers the whole run.
    whole_run: boolean;
    // Steps at which it was named; 0 when only named before the run.
    steps: number;
    // First and last step it was named at, in seconds; null when steps is 0.
    from_s: number | null;
    to_s: number | null;
}

// One execution: the inputs and parameters the simulation read plus every
// output it produced. verification and requirements are only there when the
// simulation created rules or named requirements.
export interface SimulationExecution {
    executionTime?: string;
    inputs: InputSeries[];
    parameters: Record<string, number>;
    results: InputSeries[];
    verification?: Record<string, VerificationOutcome>;
    requirements?: Record<string, RequirementCoverage>;
}

// One entry of a simulation's execution history. createdAt is UTC,
// "YYYY-MM-DD HH:MM:SS".
export interface ExecutionSummary {
    id: string;
    createdAt: string;
    status: 'completed' | 'failed' | string;
}

// One side of a what-changed comparison. timestamp is createdAt (null for
// executions stored before it was recorded).
export interface WhatChangedRun {
    run_id: string;
    timestamp: string | null;
    binary_fingerprint: string | null;
}

export interface WhatChangedSignal {
    name: string;
    in: 'both' | 'current' | 'previous';
}

export type WhatChangedValue = number | string;

// The differences between two executions of a simulation. code_changed is
// null unless both runs have a binary fingerprint. Unchanged parameters and
// CppModel.BinaryFingerprint are left out of parameters. Values are numbers,
// though the server also passes on text values (e.g. a controller variant).
export interface WhatChanged {
    current: WhatChangedRun;
    previous: WhatChangedRun;
    code_changed: boolean | null;
    parameters: {
        changed: { name: string; old: WhatChangedValue; new: WhatChangedValue }[];
        added: { name: string; new: WhatChangedValue }[];
        removed: { name: string; old: WhatChangedValue }[];
    };
    signals: {
        inputs: WhatChangedSignal[];
        outputs: WhatChangedSignal[];
    };
}

// Everything the what-changed page shows: the comparison, both executions
// and the history to pick other runs from. error is the server's message
// when there is nothing to compare.
export interface WhatChangedComparison {
    whatChanged: WhatChanged | null;
    current: Partial<SimulationExecution> | null;
    previous: Partial<SimulationExecution> | null;
    executions: ExecutionSummary[];
    error: string | null;
}

export interface UserInfo {
    id: string;
    name?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    role?: string;
}

export type UserRole = "admin" | "member";

async function getBaseUrl(): Promise<string | null> {
    if (config.apiHost) {
        return config.apiHost;
    }
    const claims = await waitForNormalizedAuthClaims({ requireWorkspaceSlug: true });
    const workspaceSlug = claims?.workspaceSlug;

    if (!workspaceSlug) {
        return null;
    }

    return `https://${workspaceSlug}.cppmodel.com/api`;
}

export type SimulationSort = 'name' | 'executions' | 'lastExecution';
export type SortOrder = 'asc' | 'desc';

export interface SimulationPage {
    items: Simulation[];
    total: number;
    limit: number;
    offset: number;
    sort: SimulationSort;
    order: SortOrder;
}

const SIMULATION_SORTS: SimulationSort[] = ['name', 'executions', 'lastExecution'];
const SORT_ORDERS: SortOrder[] = ['asc', 'desc'];

// The server's direction when none is asked for: A-Z by name, most or newest first otherwise.
export const getDefaultSortOrder = (sort: SimulationSort): SortOrder => sort === 'name' ? 'asc' : 'desc';

// The "?sort=" and "?order=" of the list page; unknown values are left out
// so the server's defaults apply.
export const getSimulationsSort = (search: URLSearchParams): { sort: SimulationSort | null; order: SortOrder | null } => {
    const sort = search.get('sort') as SimulationSort;
    const order = search.get('order') as SortOrder;
    return {
        sort: SIMULATION_SORTS.includes(sort) ? sort : null,
        order: SORT_ORDERS.includes(order) ? order : null,
    };
};

export const SIMULATIONS_PAGE_SIZE = 25;

// The 1-based page number in the list page's "?page=" query; anything else is page 1.
export const getSimulationsPageNumber = (search: URLSearchParams): number => {
    const page = Number(search.get('page'));
    return Number.isInteger(page) && page > 1 ? page : 1;
};

const getSimulationsPage = async (scope: 'user' | 'team', request: Request): Promise<SimulationPage | null> => {
    const search = new URL(request.url).searchParams;
    const page = getSimulationsPageNumber(search);
    const offset = (page - 1) * SIMULATIONS_PAGE_SIZE;
    const { sort, order } = getSimulationsSort(search);
    const query = new URLSearchParams({ scope, limit: `${SIMULATIONS_PAGE_SIZE}`, offset: `${offset}` });

    if (sort) {
        query.set('sort', sort);
    }
    if (order) {
        query.set('order', order);
    }

    const { data } = await getJson(`/simulations?${query}`);

    if (!Array.isArray(data?.items)) {
        return null;
    }

    return {
        items: data.items as Simulation[],
        total: typeof data.total === 'number' ? data.total : offset + data.items.length,
        limit: typeof data.limit === 'number' && data.limit > 0 ? data.limit : SIMULATIONS_PAGE_SIZE,
        offset: typeof data.offset === 'number' ? data.offset : offset,
        // Servers without sorting list by name
        sort: SIMULATION_SORTS.includes(data.sort) ? data.sort : 'name',
        order: SORT_ORDERS.includes(data.order) ? data.order : 'asc',
    };
}

export const getAllUserSimulations = ({ request }: { request: Request }) => getSimulationsPage('user', request);

export const getAllTeamSimulations = ({ request }: { request: Request }) => getSimulationsPage('team', request);

export const getSimulationResultsById = async (simulationId?: string): Promise<Partial<SimulationExecution> | null> => {
    if (!simulationId) {
        return null;
    }

    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/simulations/${simulationId}`, {
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).then((response) => {
        if (!response.ok) {
            return null;
        }

        return response.json();
    }).catch((error) => {
        console.error(error);
        return null;
    });

    if (!data) {
        return null;
    }

    return data;
}

// Id of the newest execution of a simulation, or null when it has none or
// the request fails. Cheap enough to poll for new executions.
export const getLatestExecutionId = async (simulationId: string): Promise<string | null> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/simulations/${simulationId}/executions?limit=1`, {
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).then((response) => {
        if (!response.ok) {
            return null;
        }

        return response.json();
    }).catch((error) => {
        console.error(error);
        return null;
    });

    const latest = data?.items?.[0]?.id;
    return latest === undefined || latest === null ? null : `${latest}`;
}

// Loader of the results page: the execution in the URL, or else the latest.
export const getSimulationResults = async ({ params }: LoaderFunctionArgs): Promise<Partial<SimulationExecution> | null> => {
    if (params.simulationId && params.executionId) {
        return getExecutionById(params.simulationId, params.executionId);
    }

    return getSimulationResultsById(params.simulationId);
}

// GET a workspace path. data is the parsed body, or null when there is none;
// on failure error is the server's message when it sent one, and status the
// HTTP status, or null when there was no response.
const getJson = async (path: string): Promise<{ data: any; error: string | null; status: number | null }> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return { data: null, error: 'The workspace is not available.', status: null };
    }

    try {
        const response = await fetch(`${host}${path}`, {
            headers: {
                Authorization: `Bearer ${user?.access_token}`,
            },
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
            const message = typeof data?.message === 'string' ? data.message : `${response.status} ${response.statusText}`;
            return { data: null, error: message, status: response.status };
        }

        return { data, error: null, status: response.status };
    } catch (error) {
        console.error(error);
        return { data: null, error: 'Could not reach the workspace.', status: null };
    }
}

// A simulation's execution history, newest first.
export const getExecutions = async (simulationId: string, limit = 100): Promise<ExecutionSummary[]> => {
    const { data } = await getJson(`/simulations/${simulationId}/executions?limit=${limit}`);
    return Array.isArray(data?.items) ? data.items : [];
}

export const getExecutionById = async (simulationId: string, executionId: string): Promise<Partial<SimulationExecution> | null> => {
    const { data } = await getJson(`/simulations/${simulationId}/executions/${executionId}`);
    return data;
}

// Compare two executions: without ids the two latest, with only current that
// one and the execution before it.
export const getWhatChanged = async (
    simulationId: string,
    current?: string | null,
    previous?: string | null
): Promise<{ data: WhatChanged | null; error: string | null }> => {
    const query = new URLSearchParams();

    if (current) {
        query.set('current', current);

        if (previous) {
            query.set('previous', previous);
        }
    }

    const search = query.toString();
    return getJson(`/simulations/${simulationId}/what-changed${search ? `?${search}` : ''}`);
}

// Loader of the what-changed page: ?current= and ?previous= pick the runs.
export const getWhatChangedComparison = async ({ params, request }: LoaderFunctionArgs): Promise<WhatChangedComparison> => {
    const simulationId = params.simulationId ?? '';
    const search = new URL(request.url).searchParams;

    const [{ data: whatChanged, error }, executions] = await Promise.all([
        getWhatChanged(simulationId, search.get('current'), search.get('previous')),
        getExecutions(simulationId),
    ]);

    if (!whatChanged) {
        return { whatChanged: null, current: null, previous: null, executions, error: error ?? 'Nothing to compare.' };
    }

    const [current, previous] = await Promise.all([
        getExecutionById(simulationId, whatChanged.current.run_id),
        getExecutionById(simulationId, whatChanged.previous.run_id),
    ]);

    return { whatChanged, current, previous, executions, error: null };
}

const isTimeSeries = (value: unknown): value is TimeSeriesInput => {
    return typeof value === 'object'
        && value !== null
        && Array.isArray((value as TimeSeriesInput).x)
        && Array.isArray((value as TimeSeriesInput).y);
};

// The server stores whatever was posted, so a pending document may still be in
// the old flat {"<name>": {x, y}, "<name>": value} format. Read both shapes
// and drop anything the simulation could not read anyway.
const parseInputsDocument = (data: unknown): InputsDocument | null => {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        return null;
    }

    const document = data as Record<string, unknown>;
    const inputs: InputSeries[] = [];
    const parameters: Record<string, number> = {};

    if (Array.isArray(document.inputs) || typeof document.parameters === 'object') {
        (Array.isArray(document.inputs) ? document.inputs : []).forEach((input) => {
            if (isTimeSeries(input) && typeof (input as InputSeries).label === 'string') {
                inputs.push(input as InputSeries);
            }
        });

        Object.entries((document.parameters ?? {}) as Record<string, unknown>).forEach(([name, value]) => {
            if (typeof value === 'number') {
                parameters[name] = value;
            }
        });

        return {
            executionTime: typeof document.executionTime === 'string' ? document.executionTime : undefined,
            inputs,
            parameters,
        };
    }

    Object.entries(document).forEach(([name, value]) => {
        if (isTimeSeries(value)) {
            inputs.push({ label: name, x: value.x, y: value.y });
        } else if (typeof value === 'number') {
            parameters[name] = value;
        }
    });

    return { inputs, parameters };
};

// The calling user's inputs document waiting for their next execution, or
// null when nothing is pending (404) - the usual case, since every execution
// consumes its submitter's document.
export const getSimulationInputs = async (simulationId: string): Promise<InputsDocument | null> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/simulations/${simulationId}/inputs`, {
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).then(async (response) => {
        if (!response.ok) {
            return null;
        }

        return response.json();
    }).catch((error) => {
        console.error(error);
        return null;
    });

    return parseInputsDocument(data);
}

// "DD-MM-YYYY HH:MM:SS" in local time, the format of executionTime.
const formatExecutionTime = (date: Date): string => {
    const pad = (value: number) => value.toString().padStart(2, '0');

    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} `
        + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// Replace the calling user's pending inputs document for this simulation; their
// next execution consumes it. Every input and parameter the run should see must
// be in the document: a name left out uses the fallback in the simulation's
// code. The server doesn't validate the document, so callers must.
export const saveSimulationInputs = async (
    simulationId: string,
    document: InputsDocument
): Promise<boolean> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return false;
    }

    const response = await fetch(`${host}/simulations/${simulationId}/inputs`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            executionTime: formatExecutionTime(new Date()),
            inputs: document.inputs.map(({ label, x, y }) => ({ label, x, y })),
            parameters: document.parameters,
        }),
    }).catch((error) => {
        console.error(error);
        return null;
    });

    if (!response || !response.ok) {
        return false;
    }

    return true;
}

// A snapshot (signal export) of one execution, as the server builds it: the
// chosen signals with the time range and transforms applied, change-compressed,
// y null where the stored value was not a number. createdAt and expiresAt are
// UTC, "YYYY-MM-DD HH:MM:SS"; from and to are ms. Snapshots are created
// through the API by other tools.
export interface SignalExport {
    id: string;
    simulationId: string;
    executionId: string;
    createdAt: string;
    expiresAt: string;
    from: number | null;
    to: number | null;
    signals: {
        label: string;
        source: 'inputs' | 'results';
        scale: number;
        xOffset: number;
        yOffset: number;
        x: number[];
        y: (number | null)[];
    }[];
    parameters: Record<string, unknown>;
}

export interface SignalSnapshot {
    snapshot: SignalExport | null;
    error: string | null;
    // The sign-in has expired or was not accepted.
    isUnauthorized?: boolean;
}

export const getSignalSnapshot = async ({ params }: LoaderFunctionArgs): Promise<SignalSnapshot> => {
    const { data, error, status } = await getJson(`/exports/${encodeURIComponent(params.snapshotId ?? '')}`);

    if (!data || !Array.isArray(data.signals)) {
        return { snapshot: null, error: error ?? 'The snapshot could not be read.', isUnauthorized: status === 401 };
    }

    return {
        snapshot: { ...data, parameters: data.parameters && typeof data.parameters === 'object' ? data.parameters : {} } as SignalExport,
        error: null,
    };
}

export const deleteSimulation = async (simulationId: string): Promise<boolean> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return false;
    }
    const response = await fetch(`${host}/simulations/${simulationId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).catch((error) => {
        console.error(error);
        return null;
    });

    if (!response || !response.ok) {
        return false;
    }

    return true;
}

export const postCreateSimulation = async ({ params }: any): Promise<any[] | null> => {
    const { simulationName, language, buildEnvironment, simulationTime, stepSize } = params;
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    try {
        const response = await fetch(`${host}/generateProject`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${user?.access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                simulationName,
                simulationTime: simulationTime,
                simulationStepSize: stepSize,
                project: { language, buildSystem: 'CMake' },
                buildEnvironment,
                inputFiles: [],
            }),
        });

        if (!response.ok) {
            const message = await response.text().catch(() => "");
            throw new Error(`Network response was not ok: ${response.status} ${response.statusText} ${message}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a") as HTMLAnchorElement;
        a.href = url;
        a.download = simulationName + ".zip";
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("Download error:", err);
    }

    return null;
}

export const getUsers = async (): Promise<UserInfo[] | null> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/users`, {
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).then(async (response) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - GET /users status", response.status, response.statusText);
        // END TEMPORARY DEBUG LOGGING.

        const jsonData = await response.json();

        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - GET /users payload", jsonData);
        console.log("TEMP DEBUG - GET /users members", jsonData?.items ?? null);
        // END TEMPORARY DEBUG LOGGING.

        return jsonData?.items ?? null;
    }).catch((error) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - GET /users fetch error", error);
        // END TEMPORARY DEBUG LOGGING.
        console.error(error);
        return null;
    });

    // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
    console.log("TEMP DEBUG - GET /users final data", data);
    // END TEMPORARY DEBUG LOGGING.

    if (!data) {
        return null;
    }

    return data as UserInfo[];
}

export const createUser = async (email: string, role: string): Promise<UserInfo | null> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/users`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
    }).then(async (response) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - POST /users request body", { email, role });
        console.log("TEMP DEBUG - POST /users status", response.status, response.statusText);
        // END TEMPORARY DEBUG LOGGING.

        if (!response.ok) {
            throw new Error(`Failed to create user: ${response.status}`);
        }

        const jsonData = await response.json();

        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - POST /users payload", jsonData);
        // END TEMPORARY DEBUG LOGGING.

        return jsonData;
    }).catch((error) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - POST /users fetch error", error);
        // END TEMPORARY DEBUG LOGGING.
        console.error(error);
        return null;
    });

    // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
    console.log("TEMP DEBUG - POST /users final data", data);
    // END TEMPORARY DEBUG LOGGING.

    if (!data) {
        return null;
    }

    return data as UserInfo;
}

export const updateUserRole = async (userId: string, role: UserRole): Promise<UserInfo | null> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return null;
    }

    const data = await fetch(`${host}/users/${userId}`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
    }).then(async (response) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - PATCH /users request body", { userId, role });
        console.log("TEMP DEBUG - PATCH /users status", response.status, response.statusText);
        // END TEMPORARY DEBUG LOGGING.

        if (!response.ok) {
            throw new Error(`Failed to update user role: ${response.status}`);
        }

        const jsonData = await response.json();

        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - PATCH /users payload", jsonData);
        // END TEMPORARY DEBUG LOGGING.

        return jsonData;
    }).catch((error) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - PATCH /users fetch error", error);
        // END TEMPORARY DEBUG LOGGING.
        console.error(error);
        return null;
    });

    // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
    console.log("TEMP DEBUG - PATCH /users final data", data);
    // END TEMPORARY DEBUG LOGGING.

    if (!data) {
        return null;
    }

    return data as UserInfo;
}

export const removeUser = async (userId: string): Promise<boolean> => {
    const user = await waitForStoredOidcUser();
    const host = await getBaseUrl();

    if (!host) {
        return false;
    }

    const response = await fetch(`${host}/users/${userId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${user?.access_token}`,
        },
    }).catch((error) => {
        // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
        console.log("TEMP DEBUG - DELETE /users fetch error", error);
        // END TEMPORARY DEBUG LOGGING.
        console.error(error);
        return null;
    });

    // TEMPORARY DEBUG LOGGING - REMOVE AFTER USER API INVESTIGATION IS DONE.
    console.log("TEMP DEBUG - DELETE /users target", userId);
    console.log("TEMP DEBUG - DELETE /users status", response?.status ?? null, response?.statusText ?? null);
    // END TEMPORARY DEBUG LOGGING.

    if (!response || !response.ok) {
        return false;
    }

    return true;
}
